import React from "react";

import {
  Box,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";

import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import MicOutlinedIcon from "@mui/icons-material/MicOutlined";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";

import {
  MEDIA_MODE,
} from "hook/useDevices";

const MediaModeOption = ({
  value,
  selected,
  icon,
  title,
  description,
  note,
  disabled,
}) => {
  return (
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        minWidth: 0,

        borderRadius: 2,

        borderColor: selected
          ? "primary.main"
          : "divider",

        borderWidth: selected ? 2 : 1,

        bgcolor: selected
          ? "rgba(2, 136, 209, 0.04)"
          : "background.paper",

        transition:
          "border-color 0.2s ease, background-color 0.2s ease",

        opacity: disabled ? 0.6 : 1,

        "&:hover": {
          borderColor: disabled
            ? undefined
            : "primary.main",
        },
      }}
    >
      <FormControlLabel
        value={value}
        disabled={disabled}
        control={
          <Radio
            sx={{
              alignSelf: "flex-start",
              mt: 0.25,
            }}
          />
        }
        label={
          <Box
            sx={{
              py: 1.5,
              pr: 1.5,
              width: "100%",
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: selected
                    ? "primary.main"
                    : "text.secondary",
                }}
              >
                {icon}
              </Box>

              <Typography
                variant="subtitle2"
                fontWeight={700}
              >
                {title}
              </Typography>
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 0.75,
                lineHeight: 1.5,
              }}
            >
              {description}
            </Typography>

            {note && (
              <Typography
                variant="caption"
                color={
                  selected
                    ? "primary.main"
                    : "text.secondary"
                }
                sx={{
                  display: "block",
                  mt: 1,
                  fontWeight: 600,
                }}
              >
                {note}
              </Typography>
            )}
          </Box>
        }
        sx={{
          m: 0,
          width: "100%",
          alignItems: "flex-start",

          "& .MuiFormControlLabel-label": {
            width: "100%",
          },
        }}
      />
    </Paper>
  );
};

const MediaModeSelector = ({
  value = MEDIA_MODE.AUDIO_VIDEO,
  onChange,
  disabled = false,
}) => {
  const handleChange = (event) => {
    const nextMode =
      event.target.value;

    if (
      nextMode !==
        MEDIA_MODE.AUDIO_VIDEO &&
      nextMode !==
        MEDIA_MODE.AUDIO_ONLY
    ) {
      return;
    }

    onChange?.(nextMode);
  };

  return (
    <FormControl
      component="fieldset"
      fullWidth
      disabled={disabled}
    >
      <Box sx={{ mb: 1.5 }}>
        <Typography
          variant="subtitle1"
          fontWeight={700}
        >
          How would you like to join?
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.25 }}
        >
          Choose how you want to communicate
          during this consultation.
        </Typography>
      </Box>

      <RadioGroup
        name="consultation-media-mode"
        value={value}
        onChange={handleChange}
      >
        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          spacing={1.5}
        >
          <MediaModeOption
            value={
              MEDIA_MODE.AUDIO_VIDEO
            }
            selected={
              value ===
              MEDIA_MODE.AUDIO_VIDEO
            }
            disabled={disabled}
            icon={
              <VideocamOutlinedIcon />
            }
            title="Audio & Video"
            description="Use your camera and microphone so your healthcare professional can see and hear you."
            note="Recommended for a normal video consultation."
          />

          <MediaModeOption
            value={
              MEDIA_MODE.AUDIO_ONLY
            }
            selected={
              value ===
              MEDIA_MODE.AUDIO_ONLY
            }
            disabled={disabled}
            icon={
              <PrivacyTipOutlinedIcon />
            }
            title="Audio Only"
            description="Use your microphone without turning on or requesting access to your camera."
            note="Privacy Mode — your camera will not be accessed."
          />
        </Stack>
      </RadioGroup>

      {value ===
        MEDIA_MODE.AUDIO_ONLY && (
        <Box
          sx={{
            mt: 1.5,
            px: 1.5,
            py: 1.25,
            borderRadius: 1.5,
            bgcolor:
              "rgba(2, 136, 209, 0.06)",
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          <MicOutlinedIcon
            color="primary"
            fontSize="small"
            sx={{ mt: 0.1 }}
          />

          <Box>
            <Typography
              variant="body2"
              fontWeight={700}
            >
              Privacy Mode enabled
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ mt: 0.25 }}
            >
              Your camera will not be
              requested. Your microphone
              and speaker still need to be
              checked before joining.
            </Typography>
          </Box>
        </Box>
      )}
    </FormControl>
  );
};

export default MediaModeSelector;